import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, FormInput, FormNumberInput } from '@/components/UI';
import { FaTimes, FaFilter, FaSave, FaSpinner } from 'react-icons/fa';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';

// No need for manual type definitions - tRPC provides them automatically

interface CreateEntityListModalProps {
    isOpen: boolean;
    onClose: () => void;
    experimentId: number;
    experimentName: string;
    duration?: string;
    onSuccess?: (entityListId: number) => void;
}

interface FilterCriteria {
    minAlphaPercent?: number;
    minProfitPercent?: number;
    minWinRate?: number;
    minWinRateVsSpy?: number;
    maxAvgLossPercent?: number;
    minTotalEntries?: number;
    minOptionProfitPercent?: number;
    minOptionWinRate?: number;
    // New median-based filters
    minMedianProfitPercent?: number;
    minMedianAlphaPercent?: number;
    minMedianOptionProfitPercent?: number;
    minMedianOptionMaxProfitPercent?: number;
    // Risk and consistency filters
    maxCoefficientOfVariation?: number;
    minProfitFactor?: number;
}

// Helper function to format time horizon
const formatTimeHorizon = (seconds: number): string => {
    const minute = 60;
    const hour = 3600;
    const day = 86400;
    const week = 604800;
    const month = 2592000;
    const year = 31536000;

    if (seconds < hour) {
        const minutes = Math.floor(seconds / minute);
        return `${minutes}m`;
    } else if (seconds < day) {
        const hours = Math.floor(seconds / hour);
        return `${hours}h`;
    } else if (seconds < week) {
        const days = Math.floor(seconds / day);
        return `${days}d`;
    } else if (seconds < month) {
        const weeks = Math.floor(seconds / week);
        return `${weeks}w`;
    } else if (seconds < year) {
        const months = Math.floor(seconds / month);
        return `${months}mo`;
    } else {
        const years = Math.floor(seconds / year);
        return `${years}y`;
    }
};

const CreateEntityListModal: React.FC<CreateEntityListModalProps> = ({
    isOpen,
    onClose,
    experimentId,
    experimentName,
    duration,
    onSuccess
}) => {
    const [listName, setListName] = useState('');
    const [description, setDescription] = useState('');
    const [filters, setFilters] = useState<FilterCriteria>({});
    const [isCreating, setIsCreating] = useState(false);
    const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
    const [selectedHorizons, setSelectedHorizons] = useState<number[]>([]);

    const [previewData, setPreviewData] = useState<{
        filteredEntities: {
            entityId: number;
            entityType: string;
            symbol: string;
            metrics: {
                avgProfitPercent: number | null;
                avgAlphaPercent: number | null;
                avgEntryEdgePercent: number | null;
                winRate: number | null;
                winRateVsSpy: number | null;
                totalEntries: number;
            };
        }[];
        totalFiltered: number;
        totalOriginal: number;
    } | null>(null);

    const createEntityListMutation = useTrpcMutation(trpc.experiment.createEntityListFromResults.mutationOptions({}), {
        successMessage: undefined, // We'll handle success messages manually
        fallbackErrorMessage: 'Failed to create entity list'
    });

    // Fetch available durations and horizons for the experiment
    const durationsQuery = useAppQuery({
        ...trpc.experiment.getDurationsAndHorizons.queryOptions({ experimentId }),
        enabled: isOpen,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    // Initialize list name with default value
    useEffect(() => {
        if (isOpen) {
            const defaultName = `High Performers - ${experimentName}`;
            setListName(defaultName);
            setDescription(`Filtered entities from experiment "${experimentName}"`);

            // Initialize with all durations/horizons selected or from URL param
            if (durationsQuery.data) {
                if (duration) {
                    // If duration filter from URL, select only that duration
                    setSelectedDurations([duration]);
                } else {
                    // Otherwise select all durations
                    setSelectedDurations(durationsQuery.data.durations);
                }

                // Select all horizons by default
                setSelectedHorizons(durationsQuery.data.horizons);
            }
        }
    }, [isOpen, experimentName, duration, durationsQuery.data]);

    // Calculate available horizons based on selected durations
    const availableHorizons = useMemo(() => {
        if (!durationsQuery.data || selectedDurations.length === 0) {
            return [];
        }

        const horizonSet = new Set<number>();

        selectedDurations.forEach((dur) => {
            const horizons = durationsQuery.data.durationHorizonMap[dur] || [];
            horizons.forEach((h: number) => horizonSet.add(h));
        });

        return Array.from(horizonSet).sort((a, b) => a - b);
    }, [selectedDurations, durationsQuery.data]);

    const handleFilterChange = (field: keyof FilterCriteria, value: number | null) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value ?? undefined
        }));

        setPreviewData(null); // Clear preview when filters change
    };

    const handlePreview = async () => {
        try {
            setIsCreating(true);

            const result = await createEntityListMutation.mutateAsync({
                experimentId,
                name: 'preview', // Temporary name for preview
                filters,
                durations: selectedDurations.length > 0 ? selectedDurations : duration ? [duration] : undefined,
                horizons: selectedHorizons.length > 0 ? selectedHorizons : undefined,
                previewOnly: true // Prevent saving to database during preview
            });

            if (result.success) {
                setPreviewData({
                    filteredEntities: result.filteredEntities,
                    totalFiltered: result.totalFiltered,
                    totalOriginal: result.totalOriginal
                });
                // No success toast for preview - just show the data
            } else {
                showToast(result.errorMessages?.[0] || 'Failed to preview filtered entities', 'error');
            }
        } catch (error) {
            showToast('Failed to preview filtered entities', 'error');
            console.error('Preview error:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const handleCreate = async () => {
        if (!listName.trim()) {
            showToast('Please enter a name for the entity list', 'error');
            return;
        }

        try {
            setIsCreating(true);

            const result = await createEntityListMutation.mutateAsync({
                experimentId,
                name: listName,
                description: description || undefined,
                filters,
                durations: selectedDurations.length > 0 ? selectedDurations : duration ? [duration] : undefined,
                horizons: selectedHorizons.length > 0 ? selectedHorizons : undefined
            });

            if (result.success) {
                showToast(`Entity list created with ${result.totalFiltered} entities`, 'success');

                if (result.entityListId && onSuccess) {
                    onSuccess(result.entityListId);
                }

                onClose();
            } else {
                showToast(result.errorMessages?.[0] || 'Failed to create entity list', 'error');
            }
        } catch (error) {
            showToast('Failed to create entity list', 'error');
            console.error('Create error:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const formatPercent = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return `${value.toFixed(2)}%`;
    };

    const formatWinRate = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return `${(value * 100).toFixed(1)}%`;
    };

    return (
        <Modal isOpen={isOpen} size="xl" onClose={onClose}>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-white">Create Entity List from Results</h2>
                    <button className="text-zinc-400 hover:text-white" onClick={onClose}>
                        <FaTimes size={20} />
                    </button>
                </div>

                {/* List Details */}
                <div className="mb-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-zinc-400 mb-2" htmlFor="list-name">
                            List Name
                        </label>
                        <FormInput
                            className="w-full"
                            id="list-name"
                            placeholder="Enter entity list name"
                            value={listName}
                            onChange={(e) => setListName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-zinc-400 mb-2" htmlFor="description">
                            Description (Optional)
                        </label>
                        <FormInput
                            className="w-full"
                            id="description"
                            placeholder="Enter description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                </div>

                {/* Duration and Horizon Selection */}
                {durationsQuery.data && (
                    <div className="mb-6 space-y-4">
                        {/* Duration Selection */}
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">Select Durations</h3>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    className="text-sm text-blue-400 hover:text-blue-300 underline"
                                    onClick={() => setSelectedDurations(durationsQuery.data.durations)}
                                >
                                    Select All
                                </button>
                                <button
                                    className="text-sm text-blue-400 hover:text-blue-300 underline"
                                    onClick={() => setSelectedDurations([])}
                                >
                                    Clear All
                                </button>
                            </div>
                            <div className="grid grid-cols-6 gap-3 mt-2">
                                {durationsQuery.data.durations.map((dur: string) => (
                                    <label className="flex items-center gap-2 text-sm" key={dur}>
                                        <input
                                            checked={selectedDurations.includes(dur)}
                                            className="rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500"
                                            type="checkbox"
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedDurations([...selectedDurations, dur]);
                                                } else {
                                                    setSelectedDurations(selectedDurations.filter((d) => d !== dur));
                                                }

                                                setPreviewData(null);
                                            }}
                                        />
                                        <span className="text-white">{dur}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Horizon Selection */}
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">Select Time Horizons</h3>
                            {selectedDurations.length === 0 ? (
                                <p className="text-zinc-400 text-sm">Select at least one duration to see available horizons</p>
                            ) : (
                                <>
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            className="text-sm text-blue-400 hover:text-blue-300 underline"
                                            onClick={() => setSelectedHorizons(availableHorizons)}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            className="text-sm text-blue-400 hover:text-blue-300 underline"
                                            onClick={() => setSelectedHorizons([])}
                                        >
                                            Clear All
                                        </button>
                                        <span className="text-xs text-zinc-500">
                                            ({availableHorizons.length} horizons available for selected durations)
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-6 gap-3 mt-2 max-h-32 overflow-y-auto">
                                        {availableHorizons.map((horizon) => (
                                            <label className="flex items-center gap-2 text-sm" key={horizon}>
                                                <input
                                                    checked={selectedHorizons.includes(horizon)}
                                                    className="rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500"
                                                    type="checkbox"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedHorizons([...selectedHorizons, horizon]);
                                                        } else {
                                                            setSelectedHorizons(selectedHorizons.filter((h) => h !== horizon));
                                                        }

                                                        setPreviewData(null);
                                                    }}
                                                />
                                                <span className="text-white">{formatTimeHorizon(horizon)}</span>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Filter Criteria */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <FaFilter /> Filter Criteria
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-alpha">
                                Min Alpha %
                            </label>
                            <FormNumberInput
                                id="min-alpha"
                                placeholder="e.g., 1.5"
                                step={0.1}
                                value={filters.minAlphaPercent}
                                onValueChange={(value) => handleFilterChange('minAlphaPercent', value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-profit">
                                Min Profit %
                            </label>
                            <FormNumberInput
                                id="min-profit"
                                placeholder="e.g., 0.5"
                                step={0.1}
                                value={filters.minProfitPercent}
                                onValueChange={(value) => handleFilterChange('minProfitPercent', value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-win-rate">
                                Min Win Rate
                            </label>
                            <FormNumberInput
                                id="min-win-rate"
                                max={1}
                                min={0}
                                placeholder="e.g., 0.6"
                                step={0.01}
                                value={filters.minWinRate}
                                onValueChange={(value) => handleFilterChange('minWinRate', value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-win-rate-spy">
                                Min Win Rate vs SPY
                            </label>
                            <FormNumberInput
                                id="min-win-rate-spy"
                                max={1}
                                min={0}
                                placeholder="e.g., 0.55"
                                step={0.01}
                                value={filters.minWinRateVsSpy}
                                onValueChange={(value) => handleFilterChange('minWinRateVsSpy', value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="max-avg-loss">
                                Max Avg Loss %
                            </label>
                            <FormNumberInput
                                id="max-avg-loss"
                                placeholder="e.g., -2.0"
                                step={0.1}
                                value={filters.maxAvgLossPercent}
                                onValueChange={(value) => handleFilterChange('maxAvgLossPercent', value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-total-entries">
                                Min Total Entries
                            </label>
                            <FormNumberInput
                                integer
                                id="min-total-entries"
                                min={1}
                                placeholder="e.g., 10"
                                value={filters.minTotalEntries}
                                onValueChange={(value) => handleFilterChange('minTotalEntries', value)}
                            />
                        </div>
                    </div>

                    {/* New Median-based Filters */}
                    <div className="mt-4">
                        <h4 className="text-sm font-medium text-zinc-400 mb-2">Median Filters (more robust to outliers)</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-median-profit">
                                    Min Median Profit %
                                </label>
                                <FormNumberInput
                                    id="min-median-profit"
                                    placeholder="e.g., 0.5"
                                    step={0.1}
                                    value={filters.minMedianProfitPercent}
                                    onValueChange={(value) => handleFilterChange('minMedianProfitPercent', value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-median-alpha">
                                    Min Median Alpha %
                                </label>
                                <FormNumberInput
                                    id="min-median-alpha"
                                    placeholder="e.g., 0.5"
                                    step={0.1}
                                    value={filters.minMedianAlphaPercent}
                                    onValueChange={(value) => handleFilterChange('minMedianAlphaPercent', value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-median-option-profit">
                                    Min Median Option Profit %
                                </label>
                                <FormNumberInput
                                    id="min-median-option-profit"
                                    placeholder="e.g., 5.0"
                                    step={0.1}
                                    value={filters.minMedianOptionProfitPercent}
                                    onValueChange={(value) => handleFilterChange('minMedianOptionProfitPercent', value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-median-option-max-profit">
                                    Min Median Option Max Profit %
                                </label>
                                <FormNumberInput
                                    id="min-median-option-max-profit"
                                    placeholder="e.g., 10.0"
                                    step={0.1}
                                    value={filters.minMedianOptionMaxProfitPercent}
                                    onValueChange={(value) => handleFilterChange('minMedianOptionMaxProfitPercent', value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Consistency Filters */}
                    <div className="mt-4">
                        <h4 className="text-sm font-medium text-zinc-400 mb-2">Consistency Filters</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="max-cv">
                                    Max Coefficient of Variation
                                </label>
                                <FormNumberInput
                                    id="max-cv"
                                    placeholder="e.g., 2.0"
                                    step={0.1}
                                    value={filters.maxCoefficientOfVariation}
                                    onValueChange={(value) => handleFilterChange('maxCoefficientOfVariation', value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-profit-factor">
                                    Min Profit Factor
                                </label>
                                <FormNumberInput
                                    id="min-profit-factor"
                                    placeholder="e.g., 1.5"
                                    step={0.1}
                                    value={filters.minProfitFactor}
                                    onValueChange={(value) => handleFilterChange('minProfitFactor', value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Option-specific filters */}
                    <div className="mt-4">
                        <h4 className="text-sm font-medium text-zinc-400 mb-2">Option Filters (if applicable)</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-option-profit">
                                    Min Option Profit %
                                </label>
                                <FormNumberInput
                                    id="min-option-profit"
                                    placeholder="e.g., 5.0"
                                    step={0.1}
                                    value={filters.minOptionProfitPercent}
                                    onValueChange={(value) => handleFilterChange('minOptionProfitPercent', value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-zinc-400 mb-1" htmlFor="min-option-win-rate">
                                    Min Option Win Rate
                                </label>
                                <FormNumberInput
                                    id="min-option-win-rate"
                                    max={1}
                                    min={0}
                                    placeholder="e.g., 0.7"
                                    step={0.01}
                                    value={filters.minOptionWinRate}
                                    onValueChange={(value) => handleFilterChange('minOptionWinRate', value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Preview Section */}
                {previewData && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-white mb-2">
                            Preview: {previewData.totalFiltered} of {previewData.totalOriginal} entities
                        </h3>
                        <div className="text-sm text-zinc-400 mb-3">
                            {selectedDurations.length > 0 && <div>Durations: {selectedDurations.join(', ')}</div>}
                            {selectedHorizons.length > 0 && (
                                <div>Horizons: {selectedHorizons.map((h) => formatTimeHorizon(h)).join(', ')}</div>
                            )}
                        </div>
                        <div className="max-h-64 overflow-y-auto border border-zinc-700 rounded-lg">
                            <table className="w-full">
                                <thead className="bg-zinc-800 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2 text-sm font-medium text-zinc-400">Symbol</th>
                                        <th className="text-right p-2 text-sm font-medium text-zinc-400">Profit %</th>
                                        <th className="text-right p-2 text-sm font-medium text-zinc-400">Alpha %</th>
                                        <th
                                            className="text-right p-2 text-sm font-medium text-zinc-400"
                                            title="vs random entry into the same name, month, and horizon — null means baseline pending"
                                        >
                                            Avg Edge %
                                        </th>
                                        <th className="text-right p-2 text-sm font-medium text-zinc-400">Win Rate</th>
                                        <th className="text-right p-2 text-sm font-medium text-zinc-400">Entries</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.filteredEntities.slice(0, 20).map((entity) => (
                                        <tr className="border-t border-zinc-700" key={entity.entityId}>
                                            <td className="p-2 text-sm text-white">{entity.symbol}</td>
                                            <td className="p-2 text-sm text-right">
                                                <span
                                                    className={
                                                        entity.metrics.avgProfitPercent && entity.metrics.avgProfitPercent > 0
                                                            ? 'text-green-400'
                                                            : 'text-red-400'
                                                    }
                                                >
                                                    {formatPercent(entity.metrics.avgProfitPercent)}
                                                </span>
                                            </td>
                                            <td className="p-2 text-sm text-right">
                                                <span
                                                    className={
                                                        entity.metrics.avgAlphaPercent && entity.metrics.avgAlphaPercent > 0
                                                            ? 'text-green-400'
                                                            : 'text-red-400'
                                                    }
                                                >
                                                    {formatPercent(entity.metrics.avgAlphaPercent)}
                                                </span>
                                            </td>
                                            <td className="p-2 text-sm text-right">
                                                <span
                                                    className={
                                                        entity.metrics.avgEntryEdgePercent && entity.metrics.avgEntryEdgePercent > 0
                                                            ? 'text-green-400'
                                                            : 'text-red-400'
                                                    }
                                                >
                                                    {formatPercent(entity.metrics.avgEntryEdgePercent)}
                                                </span>
                                            </td>
                                            <td className="p-2 text-sm text-right text-zinc-300">
                                                {formatWinRate(entity.metrics.winRate)}
                                            </td>
                                            <td className="p-2 text-sm text-right text-zinc-300">{entity.metrics.totalEntries}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {previewData.filteredEntities.length > 20 && (
                                <div className="p-2 text-center text-sm text-zinc-400">
                                    ... and {previewData.filteredEntities.length - 20} more entities
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between items-center">
                    <Button className="flex items-center gap-2" disabled={isCreating} variant="secondary" onClick={handlePreview}>
                        {isCreating ? <FaSpinner className="animate-spin" /> : <FaFilter />}
                        Preview Filtered Entities
                    </Button>

                    <div className="flex gap-3">
                        <Button disabled={isCreating} variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            className="flex items-center gap-2"
                            disabled={isCreating || !listName.trim()}
                            variant="primary"
                            onClick={handleCreate}
                        >
                            {isCreating ? <FaSpinner className="animate-spin" /> : <FaSave />}
                            Create Entity List
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CreateEntityListModal;
